import React, { useState, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Button,
  FormControlLabel,
} from '@mui/material';
import { Lead } from '../redux/slices/leadSlice';
import { marketingService } from '../services/marketingService';

interface LeadSelectionListProps {
  onSelectLead: (leads: Lead[]) => void;
}

export const LeadSelectionList: React.FC<LeadSelectionListProps> = ({ onSelectLead }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const fetchedLeads = await marketingService.getLeads();
        setLeads(fetchedLeads);
      } catch (error) {
        console.error('Erro ao carregar leads:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, []);

  const handleToggleLead = (leadId: string) => {
    const newSelectedLeads = new Set(selectedLeads);
    if (newSelectedLeads.has(leadId)) {
      newSelectedLeads.delete(leadId);
    } else {
      newSelectedLeads.add(leadId);
    }
    setSelectedLeads(newSelectedLeads);
    
    const selectedLeadsList = leads.filter(lead => newSelectedLeads.has(lead.id));
    onSelectLead(selectedLeadsList);
  };

  const handleSelectAll = () => {
    if (selectedLeads.size === filteredLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(filteredLeads.map(lead => lead.id)));
    }
    
    const selectedLeadsList = leads.filter(lead => 
      selectedLeads.size === filteredLeads.length ? false : filteredLeads.map(l => l.id).includes(lead.id)
    );
    onSelectLead(selectedLeadsList);
  };

  const filteredLeads = leads.filter(lead =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Buscar por nome, email ou telefone"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={selectedLeads.size === filteredLeads.length && filteredLeads.length > 0}
            indeterminate={selectedLeads.size > 0 && selectedLeads.size < filteredLeads.length}
            onChange={handleSelectAll}
          />
        }
        label="Selecionar todos"
      />
      <List>
        {filteredLeads.map((lead) => (
          <ListItem key={lead.id} button onClick={() => handleToggleLead(lead.id)}>
            <ListItemText
              primary={lead.name}
              secondary={`${lead.email}${lead.phone ? ` | ${lead.phone}` : ''}`}
            />
            <ListItemSecondaryAction>
              <Checkbox
                edge="end"
                checked={selectedLeads.has(lead.id)}
                onChange={() => handleToggleLead(lead.id)}
              />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};
